import { Component, Input, OnChanges, inject } from '@angular/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { TenantContextService, formatInTenantZone } from 'shared/core';
import { TallyMasterSyncRunModel } from '../../Models/TallyModel';

/** One row of the refresh history, worked out once per load rather than per change-detection pass. */
export interface TallySyncRunRow {
  id: number;
  requested: string;
  kindLabel: string;
  status: string;
  tagColor: string;
  items: string;
  detail: string;
  requestedBy: string;
  /** A manual Full refresh, which re-read every master. */
  isFullSync: boolean;
}

/**
 * Refreshes of Tally master lists, newest first, as a table: when, which list, how it
 * went, how many are in the list afterwards, the full reason (which says how many were
 * read from Tally), and who asked. Shared by Tally
 * Configuration and the Tally Sync History dialog on the supplier, department and
 * store pages, so the two cannot drift apart.
 */
@Component({
  selector: 'app-tally-sync-run-table',
  standalone: true,
  imports: [NzTableModule, NzTagModule, NzTooltipModule],
  template: `
    <nz-table #runsTable [nzData]="rows" nzSize="small" [nzFrontPagination]="false" [nzShowPagination]="false"
      [nzNoResult]="emptyText" [nzFooter]="rows.length ? totalTemplate : null">
      <thead>
        <tr>
          <th nzWidth="170px">Requested</th>
          @if (showKind) {
            <th nzWidth="110px">List</th>
          }
          <th nzWidth="170px">Status</th>
          <th nzWidth="90px" class="text-right" nz-tooltip [nzTooltipTitle]="itemsTooltip">{{ itemsLabel }}</th>
          <th>Detail</th>
          <th nzWidth="150px">Requested By</th>
        </tr>
      </thead>
      <tbody>
        @for (row of runsTable.data; track row.id) {
          <tr>
            <td>{{ row.requested }}</td>
            @if (showKind) {
              <td>{{ row.kindLabel }}</td>
            }
            <td>
              <nz-tag [nzColor]="row.tagColor">{{ row.status }}</nz-tag>
              @if (row.isFullSync) {
                <nz-tag nz-tooltip nzTooltipTitle="Every master was re-read, not just the changed ones.">Full refresh</nz-tag>
              }
            </td>
            <td class="text-right">{{ row.items }}</td>
            <td class="sync-run-detail">{{ row.detail }}</td>
            <td>{{ row.requestedBy }}</td>
          </tr>
        }
      </tbody>
    </nz-table>
    <ng-template #totalTemplate>Total {{ rows.length }} items</ng-template>
  `,
  styles: [`
    .sync-run-detail { white-space: normal; word-break: break-word; }
    .text-right { text-align: right; }
  `]
})
export class TallySyncRunTableComponent implements OnChanges {
  @Input() runs: TallyMasterSyncRunModel[] | null = [];
  /** Off when every row is the same list (the history dialog on one page). */
  @Input() showKind = true;
  @Input() emptyText = 'No refresh has been requested yet.';

  rows: TallySyncRunRow[] = [];

  /**
   * The count a finished refresh reports is how many are active in the list afterwards, whatever
   * kind of refresh it was; how many were read from Tally is in the Detail column.
   */
  static readonly ITEMS_LABEL = 'In list';
  static readonly ITEMS_TOOLTIP = 'How many are in the list after this refresh. How many were read from Tally is in the Detail.';
  readonly itemsLabel = TallySyncRunTableComponent.ITEMS_LABEL;
  readonly itemsTooltip = TallySyncRunTableComponent.ITEMS_TOOLTIP;

  private readonly tenantContext = inject(TenantContextService);

  ngOnChanges(): void {
    // In the company's own time zone (CompanyMaster.TimeZone), not the browser's.
    const timeZone = this.tenantContext.timeZone();
    this.rows = TallySyncRunTableComponent.toRows(this.runs, value => formatInTenantZone(value, timeZone, 'datetime'));
  }

  static toRows(runs: TallyMasterSyncRunModel[] | null, toTime: (value: string | null) => string): TallySyncRunRow[] {
    return (runs ?? []).map(run => ({
      id: run.TallyMasterSyncRunId,
      requested: toTime(run.RequestedUtc),
      kindLabel: TallySyncRunTableComponent.kindLabel(run.SyncKind),
      status: run.DisplayStatus,
      tagColor: TallySyncRunTableComponent.tagColor(run),
      items: run.ItemCount == null ? '—' : String(run.ItemCount),
      detail: run.Detail || '—',
      requestedBy: run.RequestedByName || '—',
      isFullSync: run.IsFullSync === true
    }));
  }

  static kindLabel(kind: string): string {
    switch (kind) {
      case 'Ledgers':
        return 'Ledgers';
      case 'Godowns':
        return 'Godowns';
      case 'StockItems':
        return 'Stock items';
      default:
        return kind;
    }
  }

  static tagColor(run: TallyMasterSyncRunModel): string {
    if (run.IsActive) {
      return 'processing';
    }
    switch (run.Status) {
      case 'Completed':
        return 'success';
      case 'Failed':
        return 'error';
      default:
        // Queued or Dispatched with no answer: the run has gone quiet.
        return 'warning';
    }
  }
}
